import type {
  RegimePublication,
  RegimePublications,
} from "@repo/shared/regimePublications";
import {
  ArrowUpRightIcon,
  FileTextIcon,
  RefreshCwIcon,
} from "lucide-react";
import { useRegimePublications } from "../../state/regimePublications";
import { Button } from "../ui/button";
import { ListSkeleton } from "./LoadingSkeletons";
import { cn } from "../../lib/utils";
import { Alert, AlertDescription } from "../ui/alert";
import { Empty, EmptyDescription, EmptyHeader } from "../ui/empty";
import { HairlineArt } from "../ui/hairline-art";
import { Globe } from "../hairline/figures";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import { externalHrefHost, safeExternalHref } from "../../lib/safeExternalHref";

const STATUS: Record<RegimePublication["status"], string> = {
  indexed: "In knowledge base",
  pending: "Awaiting indexing",
  paused: "Paused",
  failed: "Source failed",
  discovered: "Discovered · not indexed",
  blocked: "Access blocked",
  restricted: "Access restricted",
  unverified: "Not verified",
};
const KINDS: Record<string, string> = {
  primary_law: "Primary legislation",
  rulebook: "Rulebook",
  guidance: "Guidance",
  news: "News",
  consultation: "Consultation",
  register: "Register",
  standard: "Standard",
  other: "Publication",
};
const FORMATS: Record<string, string> = {
  pdf: "PDF",
  html: "Web page",
  feed: "Feed",
  mixed: "Pages & documents",
};
const date = (value: string) =>
  new Intl.DateTimeFormat("en-GB", {
    dateStyle: "medium",
    timeStyle: "short",
    timeZone: "UTC",
  }).format(new Date(value)) + " UTC";

export function discoveryCadence(
  cron: string | null,
  timezone: string | null,
): string {
  const match = cron?.match(/^(\d{1,2}) (\d{1,2}(?:,\d{1,2})*) \* \* \*$/);
  if (
    !match ||
    Number(match[1]) > 59 ||
    match[2]!.split(",").some((hour) => Number(hour) > 23)
  )
    return "Scheduled discovery";
  const times = match[2]!
    .split(",")
    .map((hour) => `${hour.padStart(2, "0")}:${match[1]!.padStart(2, "0")}`);
  return `Daily at ${times.join(", ")}${timezone ? ` ${timezone}` : ""}`;
}

function PublicationStatus({
  status,
}: {
  status: RegimePublication["status"];
}) {
  return (
    <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
      <span
        aria-hidden="true"
        className={cn(
          "size-1.5 shrink-0 rounded-full",
          status === "indexed"
            ? "bg-success"
            : status === "failed" ||
                status === "blocked" ||
                status === "restricted"
              ? "bg-warning"
              : "bg-muted-foreground/40",
        )}
      />
      {STATUS[status]}
    </span>
  );
}

export function RegimePublicationsContent({
  snapshot,
}: {
  snapshot: RegimePublications;
}) {
  const indexed = snapshot.publications.filter(
    (source) => source.status === "indexed",
  );
  return (
    <>
      <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
        Official publications and update channels Insight has mapped for this
        regime. Only indexed publications are available in the knowledge base.
      </p>
      <div className="my-4 flex flex-wrap gap-x-5 gap-y-1 text-xs text-muted-foreground tabular-nums">
        <span>
          <strong className="font-medium text-foreground">
            {snapshot.publications.length}
          </strong>{" "}
          sources mapped
        </span>
        <span>
          <strong className="font-medium text-foreground">
            {indexed.length}
          </strong>{" "}
          indexed
        </span>
        <span>
          <strong className="font-medium text-foreground">
            {indexed
              .reduce((sum, source) => sum + source.chunks, 0)
              .toLocaleString()}
          </strong>{" "}
          searchable chunks
        </span>
      </div>
      <div className="mb-4 space-y-1 text-xs text-muted-foreground">
        <p>
          Source discovery:{" "}
          {snapshot.discovery.enabled
            ? discoveryCadence(
                snapshot.discovery.cron,
                snapshot.discovery.timezone,
              )
            : snapshot.discovery.cron
              ? "Paused"
              : "Not scheduled"}
          .
        </p>
        {snapshot.discovery.nextRunAt ? (
          <p>Next scheduled discovery: {date(snapshot.discovery.nextRunAt)}.</p>
        ) : null}
      </div>
      {snapshot.publications.length === 0 ? (
        <Empty>
          <HairlineArt figure={Globe} className="w-48 sm:w-56" />
          <EmptyHeader>
            <EmptyDescription>
              No publications have been mapped for this regime yet.
            </EmptyDescription>
          </EmptyHeader>
        </Empty>
      ) : (
        <Table className="table-fixed" aria-label="Regime publications">
          <TableHeader>
            <TableRow className="bg-muted/10 hover:bg-muted/10">
              <TableHead className="h-9 px-4 text-muted-foreground">
                Publication
              </TableHead>
              <TableHead className="hidden h-9 w-48 px-4 text-muted-foreground sm:table-cell">
                Status
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {snapshot.publications.map((source) => (
              <TableRow key={source.id}>
                <TableCell className="px-4 py-3 whitespace-normal">
                  <div className="flex items-start gap-2.5">
                    <FileTextIcon
                      aria-hidden="true"
                      className="mt-0.5 size-4 shrink-0 text-muted-foreground"
                    />
                    <div className="min-w-0">
                      <p className="text-[13px] font-medium text-foreground wrap-anywhere">
                        {safeExternalHref(source.url) ? (
                          <a
                            href={safeExternalHref(source.url) ?? undefined}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
                          >
                            {source.title}
                            <ArrowUpRightIcon
                              aria-label="Opens in a new tab"
                              className="ms-1 inline size-3.5"
                            />
                          </a>
                        ) : (
                          source.title
                        )}
                      </p>
                      <p className="mt-0.5 text-[11px] text-muted-foreground wrap-anywhere">
                        {[
                          KINDS[source.kind] ?? "Publication",
                          FORMATS[source.format],
                          externalHrefHost(source.url),
                        ]
                          .filter(Boolean)
                          .join(" · ")}
                      </p>
                      <div className="mt-1.5 sm:hidden">
                        <PublicationStatus status={source.status} />
                      </div>
                      {source.why ? (
                        <p className="mt-1.5 text-xs leading-relaxed text-muted-foreground">
                          {source.why}
                        </p>
                      ) : null}
                      <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-muted-foreground tabular-nums">
                        {source.status === "indexed" ? (
                          <span>
                            {source.chunks.toLocaleString()} searchable chunks
                          </span>
                        ) : null}
                        {source.version && !/^sha256:/i.test(source.version) ? (
                          <span>Version {source.version}</span>
                        ) : null}
                      </div>
                    </div>
                  </div>
                </TableCell>
                <TableCell className="hidden px-4 py-3 align-top whitespace-normal sm:table-cell">
                  <PublicationStatus status={source.status} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </>
  );
}

export function RegimePublicationsSection({
  organizationId,
  regimeId,
}: {
  organizationId: string | null;
  regimeId: string;
}) {
  const { snapshot, failed, isPending, refresh } = useRegimePublications(
    organizationId,
    regimeId,
  );
  return (
    <section
      aria-labelledby="regime-publications-title"
      className="scroll-mt-6"
    >
      <div className="flex items-center justify-between gap-3">
        <h2
          id="regime-publications-title"
          className="text-[15px] font-semibold text-foreground"
        >
          Sources & scraping
        </h2>
        <Button
          variant="ghost"
          size="sm"
          disabled={isPending}
          onClick={refresh}
          aria-label="Refresh publication coverage"
        >
          <RefreshCwIcon className="size-3.5" /> Refresh
        </Button>
      </div>
      {failed ? (
        <Alert variant="error" className="mt-4">
          <AlertDescription>
            Publication coverage could not be loaded. Try refreshing.
          </AlertDescription>
        </Alert>
      ) : snapshot ? (
        <RegimePublicationsContent snapshot={snapshot} />
      ) : (
        <ListSkeleton className="p-5" rows={3} />
      )}
    </section>
  );
}
